import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import {
  Search, Wrench, Droplet, Zap, Wind, Truck, Laptop, Wifi, Tv, Hammer, Leaf, Sparkles,
  Home as HomeIcon, Paintbrush, Square, Grid, Grid3x3, DoorClosed, DoorOpen, Lock, Bug,
  SprayCan, CloudRain, Trash2, Flame, Scissors, TreePine, Waves, Fence, Utensils, Palette,
  Speaker, Shield, Sun, Car, Camera, Video, Music, Calendar, Dumbbell, Book, Code,
  Megaphone, Calculator, Dog, PawPrint,
} from "lucide-react";

const ICONS = {
  droplet: Droplet, zap: Zap, wind: Wind, truck: Truck, laptop: Laptop, wifi: Wifi,
  tv: Tv, speaker: Speaker, leaf: Leaf, sparkles: Sparkles, home: HomeIcon,
  paintbrush: Paintbrush, hammer: Hammer, saw: Hammer, wrench: Wrench,
  square: Square, grid: Grid, "grid-3x3": Grid3x3, "door-closed": DoorClosed, "door-open": DoorOpen,
  lock: Lock, bug: Bug, "spray-can": SprayCan, "cloud-rain": CloudRain, "trash-2": Trash2,
  flame: Flame, scissors: Scissors, "tree-pine": TreePine, waves: Waves, fence: Fence,
  utensils: Utensils, palette: Palette, shield: Shield, sun: Sun, car: Car,
  camera: Camera, video: Video, music: Music, calendar: Calendar, dumbbell: Dumbbell,
  book: Book, code: Code, megaphone: Megaphone, calculator: Calculator, dog: Dog, pawprint: PawPrint,
};

// Group categories into Thumbtack/Bark-style sections
const GROUPS = [
  { id: "home", label: "Home Repair & Trades", slugs: [
    "plumbing","electrical","hvac","handyman","carpentry","painting","roofing","drywall","concrete",
    "flooring","tile","windows","doors","garage-doors","appliance-repair","locksmith","pest-control",
  ]},
  { id: "cleaning", label: "Cleaning & Maintenance", slugs: [
    "cleaning","carpet-cleaning","pressure-washing","gutter-cleaning","junk-removal","chimney",
  ]},
  { id: "outdoor", label: "Outdoor & Landscaping", slugs: [
    "landscaping","lawn-mowing","tree-service","pool-service","fence","deck-patio",
  ]},
  { id: "remodel", label: "Remodel & Design", slugs: [
    "kitchen-remodel","bathroom-remodel","interior-design",
  ]},
  { id: "moving", label: "Moving", slugs: ["moving","long-distance-moving"]},
  { id: "tech", label: "Tech & Electronics", slugs: [
    "computer-repair","network-support","tv-mounting","home-theater","smart-home","security-systems","solar",
  ]},
  { id: "auto", label: "Auto", slugs: ["auto-repair","auto-detailing"]},
  { id: "events", label: "Events & Creative", slugs: [
    "photography","videography","dj","catering","event-planning",
  ]},
  { id: "wellness", label: "Wellness & Lessons", slugs: [
    "personal-training","massage","tutoring",
  ]},
  { id: "business", label: "Business Services", slugs: [
    "web-design","marketing","accounting",
  ]},
  { id: "pets", label: "Pet Services", slugs: ["dog-training","pet-care"]},
];

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState("all");

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  const matchesSearch = (c) => c.name.toLowerCase().includes(q.toLowerCase());
  const visibleGroups = active === "all" ? GROUPS : GROUPS.filter((g) => g.id === active);

  return (
    <div className="min-h-screen bg-bone">
      <Seo
        title="Browse Trades & Services"
        path="/categories"
        description="Plumbing, electrical, HVAC, remodeling, moving, tech installs and more — browse every trade on LeadTitan and post your project free."
      />
      <Navbar />
      <section className="border-b-2 border-ink">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
          <div className="overline mb-3">// Browse</div>
          <h1 className="font-display font-black text-5xl sm:text-6xl uppercase tracking-tighter">All categories</h1>
          <p className="text-muted-foreground mt-4 max-w-xl">
            {cats.length} trades. Pick what you need — we'll match you with vetted local pros.
          </p>
          <div className="mt-8 max-w-md flex items-center gap-2 border-2 border-ink bg-white px-4 py-3">
            <Search className="w-4 h-4" />
            <input
              className="flex-1 bg-transparent outline-none font-mono text-sm"
              placeholder="search categories..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="cat-search"
            />
          </div>
        </div>
      </section>

      <section className="border-b-2 border-ink bg-acid sticky top-[73px] z-30 overflow-x-auto">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex gap-0 border-l-2 border-r-2 border-ink">
          <button
            onClick={() => setActive("all")}
            className={`px-4 py-3 font-mono uppercase text-xs tracking-widest font-bold whitespace-nowrap border-r-2 border-ink ${active === "all" ? "bg-ink text-acid" : "bg-acid hover:bg-white"}`}
            data-testid="group-all"
          >
            All ({cats.length})
          </button>
          {GROUPS.map((g) => {
            const count = cats.filter((c) => g.slugs.includes(c.slug)).length;
            if (count === 0) return null;
            return (
              <button
                key={g.id}
                onClick={() => setActive(g.id)}
                className={`px-4 py-3 font-mono uppercase text-xs tracking-widest font-bold whitespace-nowrap border-r-2 border-ink ${active === g.id ? "bg-ink text-acid" : "bg-acid hover:bg-white"}`}
                data-testid={`group-${g.id}`}
              >
                {g.label} ({count})
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 space-y-12">
          {visibleGroups.map((g) => {
            const groupCats = cats.filter((c) => g.slugs.includes(c.slug) && matchesSearch(c));
            if (groupCats.length === 0) return null;
            return (
              <div key={g.id}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="overline">// {g.label}</span>
                  <span className="font-mono text-xs">{groupCats.length}</span>
                  <div className="flex-1 h-px bg-ink/30" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0 border-2 border-ink">
                  {groupCats.map((c) => {
                    const Icon = ICONS[c.icon] || Wrench;
                    return (
                      <div key={c.category_id} className="border-r-2 border-b-2 border-ink p-6 bg-white hover:bg-acid transition-colors group" data-testid={`cat-tile-${c.slug}`}>
                        <Icon className="w-6 h-6 mb-3" />
                        <Link to={`/trades/${c.slug}`} className="font-display font-bold uppercase text-lg leading-tight hover:underline block" data-testid={`cat-link-${c.slug}`}>{c.name}</Link>
                        <div className="font-mono text-xs mt-3 flex justify-between">
                          <span>${c.lead_price.toFixed(0)} / lead</span>
                        </div>
                        <Link
                          to="/customer/post"
                          state={{ category_id: c.category_id }}
                          className="brut-btn w-full justify-center mt-4 text-[10px]"
                          data-testid={`cat-post-${c.slug}`}
                        >
                          Post a job
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {/* Show ungrouped categories (admin-added) */}
          {active === "all" && (() => {
            const groupedSlugs = new Set(GROUPS.flatMap((g) => g.slugs));
            const other = cats.filter((c) => !groupedSlugs.has(c.slug) && matchesSearch(c));
            if (other.length === 0) return null;
            return (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="overline">// Other</span>
                  <span className="font-mono text-xs">{other.length}</span>
                  <div className="flex-1 h-px bg-ink/30" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0 border-2 border-ink">
                  {other.map((c) => {
                    const Icon = ICONS[c.icon] || Wrench;
                    return (
                      <div key={c.category_id} className="border-r-2 border-b-2 border-ink p-6 bg-white hover:bg-acid transition-colors" data-testid={`cat-tile-${c.slug}`}>
                        <Icon className="w-6 h-6 mb-3" />
                        <Link to={`/trades/${c.slug}`} className="font-display font-bold uppercase text-lg leading-tight hover:underline block" data-testid={`cat-link-${c.slug}`}>{c.name}</Link>
                        <div className="font-mono text-xs mt-3 flex justify-between">
                          <span>${c.lead_price.toFixed(0)} / lead</span>
                        </div>
                        <Link to="/customer/post" state={{ category_id: c.category_id }} className="brut-btn w-full justify-center mt-4 text-[10px]" data-testid={`cat-post-${c.slug}`}>
                          Post a job
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </section>
      <Footer />
    </div>
  );
}
